/**
 * Access Log UUID Management
 *
 * Manages the access_log_uuid in sessionStorage for tracking participant
 * distribution access. Uses sessionStorage so that:
 * - Page refreshes in the same tab reuse the same UUID (same "click")
 * - New tabs/windows create new UUIDs (new "click")
 *
 * The UUID is keyed by the distribution token to support multiple
 * distributions being accessed in different tabs.
 */

const ACCESS_LOG_PREFIX = "access_log_uuid_";

/**
 * Get the access log UUID for a given distribution token
 * @param {string} token - The distribution token
 * @returns {string|null} The UUID if it exists, null otherwise
 */
const getAccessLogUuid = (token) => {
  if (!token) return null;
  const key = `${ACCESS_LOG_PREFIX}${token}`;
  return sessionStorage.getItem(key);
};

/**
 * Set the access log UUID for a given distribution token
 * @param {string} token - The distribution token
 * @param {string} uuid - The access log UUID to store
 */
const setAccessLogUuid = (token, uuid) => {
  if (!token || !uuid) return;
  const key = `${ACCESS_LOG_PREFIX}${token}`;
  sessionStorage.setItem(key, uuid);
};

/**
 * Clear the access log UUID for a given distribution token
 * @param {string} token - The distribution token
 */
const clearAccessLogUuid = (token) => {
  if (!token) return;
  const key = `${ACCESS_LOG_PREFIX}${token}`;
  sessionStorage.removeItem(key);
};

/**
 * Clear all access log UUIDs from sessionStorage
 * Useful for complete session cleanup
 */
const clearAllAccessLogUuids = () => {
  const keys = Object.keys(sessionStorage);
  keys.forEach((key) => {
    if (key.startsWith(ACCESS_LOG_PREFIX)) {
      sessionStorage.removeItem(key);
    }
  });
};

export const accessLog = {
  getAccessLogUuid,
  setAccessLogUuid,
  clearAccessLogUuid,
  clearAllAccessLogUuids,
};
